import React, {useEffect, useMemo} from 'react';
import {Box, Divider, HStack, VStack} from 'native-base';
import AppText from '../../../../../../common/AppText';
import {t} from '../../../../../../../services/i18n';
import {Colors} from '../../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../../styles/GlobalStyles';
import {useDispatch, useSelector} from 'react-redux';
import {getCategoriesByTypes} from '../../../../../../../redux/ScheduleSlice';
import {CLASS, HUGIM} from '../../../../../../../services/ConstHelper';
import {isMobile} from '../../../../../../../services/Helpers';
import AdvanceSelect from '../../../../../../common/AdvanceSelect';
import SimpleInput from '../../../../../../common/SimpleInput';
import CustomButtonV2 from '../../../../../../common/CustomButtonV2';
import InfoTooltip from '../../../../../../common/InfoTooltip';
import {GlobalConsts} from '../../../../../../../styles/GlobalConsts';

const BLANK_ROW = {box_categories_id: null, trial_limit: null};
const SERVICE_TYPE_WIDTH = '250px';
const SESSIONS_WIDTH = '60px';
const TRASH_COLUMN_WIDTH = '2rem';

const sortServiceTypes = (categories) => {
    return [...categories].sort((a, b) => {
        if (a.active !== b.active) {
            return b.active - a.active;
        }
        return a.name.localeCompare(b.name, undefined, {sensitivity: 'base'});
    });
};

const TrialLimitPerServiceType = (props) => {
    const {values, setFieldValue, errors, submitCount} = props;
    const eventTypes = useSelector(state => state.schedule.eventTypes.data);
    const dispatch = useDispatch();
    const rows = values.trialServiceTypeLimits ?? [BLANK_ROW];
    const showTrashColumn = rows.length > 1;
    const rowGap = isMobile ? '0.75rem' : '0.625rem';
    const columnHeaderFontSize = isMobile ? GlobalStyleAttributes.fonts.standard16 : GlobalStyleAttributes.fonts.infoText;

    useEffect(() => {
        if (!eventTypes) {
            dispatch(getCategoriesByTypes(false));
        }
    }, [eventTypes]);

    useEffect(() => {
        if (!values.trialServiceTypeLimits) {
            setFieldValue('trialServiceTypeLimits', [BLANK_ROW]);
        }
    }, [values.trialServiceTypeLimits]);

    const allServiceTypes = useMemo(() => {
        if (!eventTypes) {
            return [];
        }
        const categories = [...(eventTypes[CLASS] ?? []), ...(eventTypes[HUGIM] ?? []),];
        return sortServiceTypes(categories).map(category => ({
            ...category,
            name: category.active ? category.name : `${category.name} (${t('inactive')})`,
        }));
    }, [eventTypes]);

    const getOptionsForRow = (rowIndex) => {
        const selectedElsewhere = rows.map((row, index) => (index !== rowIndex ? row.box_categories_id : null)).filter(Boolean);
        return allServiceTypes.filter(category => !selectedElsewhere.includes(category.id)).map(category => ({
                ...category,
                label: category.name,
                value: category.id,
            }));
    };

    const usedServiceTypeCount = rows.filter(row => row.box_categories_id).length;
    const canAddRow = allServiceTypes.length > 0 && usedServiceTypeCount < allServiceTypes.length;

    const addRow = () => {
        setFieldValue('trialServiceTypeLimits', [...rows, {...BLANK_ROW}]);
    };

    const removeRow = (index) => {
        const nextRows = rows.filter((_, i) => i !== index);
        setFieldValue('trialServiceTypeLimits', nextRows.length ? nextRows : [{...BLANK_ROW}]);
    };

    const getFieldError = (index, field) => {
        if (!(submitCount > 0)) {
            return null;
        }
        const rowErrors = errors.trialServiceTypeLimits?.[index];
        if (rowErrors && rowErrors[field]) {
            return rowErrors[field];
        }
        return null;
    };

    const renderRow = (row, index) => (
        <HStack width={'100%'} alignItems={'center'} py={'0.75rem'} space={rowGap} borderBottomWidth={1} borderBottomColor={Colors.borderColor}>
            <Box
                width={isMobile ? undefined : SERVICE_TYPE_WIDTH}
                flex={isMobile ? 1 : undefined}
                minWidth={isMobile ? 0 : undefined}
            >
                <AdvanceSelect
                    options={getOptionsForRow(index)}
                    initValue={row.box_categories_id}
                    onChange={(selected) => setFieldValue(`trialServiceTypeLimits[${index}].box_categories_id`, selected?.id ?? null)}
                    placeholder={t('serviceTypeLbl')}
                    hideCreateBtn
                    isClearable={true}
                    isError={!!getFieldError(index, 'box_categories_id')}
                    customStyle={isMobile ? {width: '100%'} : undefined}
                />
            </Box>
            <Box width={SESSIONS_WIDTH} flexShrink={0}>
                <SimpleInput
                    value={row.trial_limit}
                    onChangeCallback={(val) => setFieldValue(`trialServiceTypeLimits[${index}].trial_limit`, val)}
                    width={'100%'}
                    numericOnly={true}
                    styleOverride={{height: '38px', width: '100%', alignContent: 'center'}}
                    status={getFieldError(index, 'trial_limit') ? 'error' : null}
                />
            </Box>
            {showTrashColumn && (
                <Box width={TRASH_COLUMN_WIDTH} flexShrink={0} alignItems={'center'} justifyContent={'center'}>
                    {index > 0 && (
                        <CustomButtonV2
                            type={'link'}
                            iconName={'fa-trash'}
                            onClick={() => removeRow(index)}
                        />
                    )}
                </Box>
            )}
        </HStack>
        
    );

    return (
        <VStack space={'1rem'} width={'100%'}>
            <HStack alignItems={'center'}>
                <AppText boldFont>{t('trialLimitPerServiceTypeHeader')}</AppText>
                {!isMobile && <InfoTooltip title={t('trialLimitPerServiceTypeTooltip')}/>}
            </HStack>

            <VStack width={'100%'}>
                <HStack width={'100%'} alignItems={'center'} pb={'0.35rem'} space={rowGap}>
                    <Box
                        width={isMobile ? undefined : SERVICE_TYPE_WIDTH}
                        flex={isMobile ? 1 : undefined}
                        minWidth={isMobile ? 0 : undefined}
                    >
                        <AppText fontSize={columnHeaderFontSize} mediumFont>{t('serviceTypeLbl')}</AppText>
                    </Box>
                    <Box width={SESSIONS_WIDTH} flexShrink={0}>
                        <AppText fontSize={columnHeaderFontSize} mediumFont>{t('sessionsLbl')}</AppText>
                    </Box>
                    {showTrashColumn && <Box width={TRASH_COLUMN_WIDTH} flexShrink={0}/>}
                </HStack>

                <Divider width={'100%'} bg={Colors.borderColor}/>

                {rows.map((row, index) => renderRow(row, index))}
            </VStack>

            {canAddRow && (
                <CustomButtonV2
                    onClick={addRow}
                    text={t('trialLimitAddAnother')}
                    applyHoverOnText={true}
                    type={'link'}
                    iconName={'fa-plus'}
                    iconProps={{
                        iconColor: Colors.newDesign.mainBlue,
                        iconHoverColor: Colors.newDesign.mainBlueHover,
                        iconSize: GlobalConsts.iconSize.XSS,
                        iconPrefix: GlobalConsts.iconPrefix.REGULAR,
                        restIconStyle: {marginBottom: '1px'},
                    }}
                    textProps={{color: Colors.newDesign.mainBlue}}
                    wrapperStyle={{width: 'max-content'}}
                    style={{ marginInlineEnd: '6px' }}
                />
            )}
        </VStack>
    );
};

export default TrialLimitPerServiceType;
